import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import FileUploader from "@/components/FileUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const DOC_TYPES = ["Critical", "Non-Critical"];

const empty = {
  title: "",
  category: "",
  document_type: "Non-Critical",
  related_entity: "",
  document_date: "",
  expiry_date: "",
  notes: "",
  file_url: "",
  file_public_id: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

const TypePill = ({ t }) => {
  const map = {
    Critical: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
    "Non-Critical": "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${map[t] || ""}`}>
      {t || "—"}
    </span>
  );
};

const isExpired = (d) => d && new Date(d) < new Date();
const isExpiringSoon = (d) => {
  if (!d) return false;
  const days = (new Date(d) - new Date()) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= 30;
};

export default function TrustDocuments() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/documents"),
      api.get("/masters/document_category"),
      api.get("/masters/related_entity"),
    ]);
    setItems(a.data);
    setCategories(b.data);
    setEntities(c.data);
  };

  useEffect(() => {
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      ...empty,
      ...d,
      document_date: d.document_date || "",
      expiry_date: d.expiry_date || "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      document_date: form.document_date || null,
      expiry_date: form.expiry_date || null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/documents/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Document updated");
      } else {
        const { data } = await api.post("/documents", payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Document added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const d = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/documents/${d.id}`);
      setItems((arr) => arr.filter((x) => x.id !== d.id));
      toast.success("Document removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const criticalCount = items.filter((x) => x.document_type === "Critical").length;
  const expiringCount = items.filter((x) => isExpiringSoon(x.expiry_date)).length;

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Records</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Trust Official Documents</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Statutory records, compliance certificates, agreements and other PDFs (up to 25 MB each)
            archived against trust entities.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="document-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} /> Upload Document
        </button>
      </div>

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
              Total documents
            </div>
            <div className="font-serif text-3xl text-[#1c1a17] mt-1">{items.length}</div>
          </div>
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
              Critical
            </div>
            <div className="font-serif text-3xl text-[#9e2a2b] mt-1">{criticalCount}</div>
          </div>
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
              Expiring within 30 days
            </div>
            <div className="font-serif text-3xl text-[#d4af37] mt-1">{expiringCount}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="documents-empty"
        >
          <FileText size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No documents on file</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Upload the first official document — trust deed, registration certificate, audit report, etc.
          </div>
          <button
            onClick={openCreate}
            data-testid="document-empty-add-button"
            className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Upload first document
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Title", "Category", "Type", "Related Entity", "Date", "Expiry", "File", ""].map(
                  (h, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`document-row-${d.id}`}
                >
                  <td className="px-4 py-4 text-[#1c1a17]">{d.title}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.category || "—"}</td>
                  <td className="px-4 py-4">
                    <TypePill t={d.document_type} />
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.related_entity || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.document_date || "—"}</td>
                  <td className="px-4 py-4">
                    {d.expiry_date ? (
                      <span
                        className={`inline-flex items-center gap-1 ${
                          isExpired(d.expiry_date)
                            ? "text-[#9e2a2b]"
                            : isExpiringSoon(d.expiry_date)
                              ? "text-[#8a6d1d]"
                              : "text-[#6b655b]"
                        }`}
                      >
                        {(isExpired(d.expiry_date) || isExpiringSoon(d.expiry_date)) && (
                          <AlertTriangle size={12} strokeWidth={1.5} />
                        )}
                        {d.expiry_date}
                      </span>
                    ) : (
                      <span className="text-[#6b655b]/60">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {d.file_url ? (
                      <a
                        href={d.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#234333] hover:underline text-xs"
                      >
                        Open <ExternalLink size={11} strokeWidth={1.5} />
                      </a>
                    ) : (
                      <span className="text-[#6b655b]/60 text-xs">No file</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEdit(d)}
                      data-testid={`document-edit-${d.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(d)}
                      data-testid={`document-delete-${d.id}`}
                      className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="document-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Document" : "Upload Trust Document"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Document Title *" full>
              <input
                data-testid="document-title-input"
                className={inputCls}
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </Field>
            <Field label="Document Category">
              <select
                data-testid="document-category-select"
                className={inputCls}
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                <option value="">— Select —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Document Type">
              <select
                data-testid="document-type-select"
                className={inputCls}
                value={form.document_type}
                onChange={(e) => set("document_type", e.target.value)}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Related Entity">
              <select
                data-testid="document-entity-select"
                className={inputCls}
                value={form.related_entity}
                onChange={(e) => set("related_entity", e.target.value)}
              >
                <option value="">— Select —</option>
                {entities.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Document Date">
              <input
                data-testid="document-date-input"
                type="date"
                className={inputCls}
                value={form.document_date}
                onChange={(e) => set("document_date", e.target.value)}
              />
            </Field>
            <Field label="Expiry Date (if any)">
              <input
                data-testid="document-expiry-input"
                type="date"
                className={inputCls}
                value={form.expiry_date}
                onChange={(e) => set("expiry_date", e.target.value)}
              />
            </Field>
            <Field label="Notes" full>
              <textarea
                data-testid="document-notes-input"
                className={inputCls + " min-h-[72px] resize-y"}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>

            <div className="md:col-span-2 pt-1">
              <FileUploader
                kind="trust_document"
                label="Document File"
                url={form.file_url}
                testid="document-file"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, file_url: url, file_public_id: public_id }))
                }
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="document-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="document-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this document?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.title} will be permanently removed and the uploaded PDF will be deleted from storage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="document-delete-cancel"
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="document-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
